import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Save, Send, GraduationCap, BookOpen, AlertTriangle, Loader2 } from 'lucide-react';

const CreateJobPage = () => {
  const { jobId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!jobId;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [constants, setConstants] = useState(null);
  const [quota, setQuota] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    department: '',
    job_type: 'full_time',
    salary_min: '',
    salary_max: '',
    experience_years: '',
    skills: [],
    // Education-specific
    job_category: '',
    job_subcategory: '',
    subjects: [],
    grade_levels: [],
    board_curriculum: [],
    required_certifications: [],
    preferred_certifications: [],
    demo_lesson_required: false,
    accommodation_provided: false,
    transport_provided: false
  });
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    // Fetch education constants
    const fetchConstants = async () => {
      try {
        const response = await axios.get(`${API}/education/constants`);
        setConstants(response.data);
      } catch (error) {
        console.error('Failed to fetch constants:', error);
      }
    };
    fetchConstants();
    // Fetch quota info for non-edit flows
    if (!isEdit && token) {
      const headers = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/tenant/check-quota/job`, { headers, withCredentials: true })
        .then(res => setQuota(res.data))
        .catch(() => {});
    }
  }, [token, isEdit]);

  useEffect(() => {
    if (isEdit) {
      const fetchJob = async () => {
        try {
          const response = await axios.get(`${API}/jobs/${jobId}`);
          const job = response.data;
          setFormData({
            title: job.title || '',
            description: job.description || '',
            location: job.location || '',
            department: job.department || '',
            job_type: job.job_type || 'full_time',
            salary_min: job.salary_min?.toString() || '',
            salary_max: job.salary_max?.toString() || '',
            experience_years: job.experience_years?.toString() || '',
            skills: job.skills || [],
            job_category: job.job_category || '',
            job_subcategory: job.job_subcategory || '',
            subjects: job.subjects || [],
            grade_levels: job.grade_levels || [],
            board_curriculum: job.board_curriculum || [],
            required_certifications: job.required_certifications || [],
            preferred_certifications: job.preferred_certifications || [],
            demo_lesson_required: job.demo_lesson_required || false,
            accommodation_provided: job.accommodation_provided || false,
            transport_provided: job.transport_provided || false
          });
        } catch (error) {
          toast.error('Failed to load job');
          navigate('/jobs');
        } finally {
          setFetching(false);
        }
      };
      fetchJob();
    }
  }, [jobId, isEdit, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleSubmit = async (publish = false) => {
    if (!formData.title || !formData.description || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null
      };

      let newJobId = jobId;
      if (isEdit) {
        await axios.put(`${API}/jobs/${jobId}`, payload, { headers, withCredentials: true });
      } else {
        const response = await axios.post(`${API}/jobs`, payload, { headers, withCredentials: true });
        newJobId = response.data.job_id;
      }

      if (publish) {
        await axios.put(`${API}/jobs/${newJobId}/status?status=published`, {}, { headers, withCredentials: true });
        toast.success('Job published successfully!');
      } else {
        toast.success(isEdit ? 'Job updated!' : 'Job saved as draft');
      }

      navigate(`/jobs/${newJobId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  const getSubcategories = () => {
    if (!constants || !formData.job_category) return [];
    return constants.job_categories[formData.job_category]?.subcategories || [];
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="create-job-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              {isEdit ? 'Edit Job' : 'Create Job Posting'}
            </h1>
          </div>
        </div>

        {/* Quota Warning Banner */}
        {!isEdit && quota && !quota.allowed && (
          <Card className="border-amber-200 bg-amber-50" data-testid="quota-exceeded-banner">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Job posting limit reached</p>
                <p className="text-sm text-amber-600">
                  Your plan allows {quota.limits?.jobs?.unlimited ? 'unlimited' : quota.limits?.jobs?.limit} job {quota.limits?.jobs?.limit === 1 ? 'posting' : 'postings'}.
                  You currently have {quota.limits?.jobs?.current} active.
                </p>
              </div>
              <Button size="sm" onClick={() => navigate('/pricing')} data-testid="upgrade-plan-btn">Upgrade Plan</Button>
            </CardContent>
          </Card>
        )}

        {/* Quota Usage Indicator */}
        {!isEdit && quota?.allowed && quota?.limits?.jobs && !quota.limits.jobs.unlimited && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="quota-usage-indicator">
            <span>{quota.limits.jobs.current} / {quota.limits.jobs.limit} job postings used</span>
            <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  quota.limits.jobs.current / quota.limits.jobs.limit > 0.8 ? 'bg-amber-500' : 'bg-indigo-500'
                }`}
                style={{ width: `${Math.min(100, (quota.limits.jobs.current / quota.limits.jobs.limit) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Basic Details */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g. Senior Mathematics Teacher"
                className="mt-1"
                data-testid="job-title-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Job Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe the role, responsibilities, and requirements..."
                className="mt-1 min-h-[200px]"
                data-testid="job-description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g. Mumbai, Maharashtra"
                  className="mt-1"
                  data-testid="job-location-input"
                />
              </div>
              <div>
                <Label>Job Type</Label>
                <Select value={formData.job_type} onValueChange={(v) => handleChange('job_type', v)}>
                  <SelectTrigger className="mt-1" data-testid="job-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="salary_min">Salary Min (₹/month)</Label>
                <Input
                  id="salary_min"
                  type="number"
                  value={formData.salary_min}
                  onChange={(e) => handleChange('salary_min', e.target.value)}
                  placeholder="e.g. 30000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="salary_max">Salary Max (₹/month)</Label>
                <Input
                  id="salary_max"
                  type="number"
                  value={formData.salary_max}
                  onChange={(e) => handleChange('salary_max', e.target.value)}
                  placeholder="e.g. 50000"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="experience">Experience (Years)</Label>
                <Input
                  id="experience"
                  type="number"
                  min="0"
                  value={formData.experience_years}
                  onChange={(e) => handleChange('experience_years', e.target.value)}
                  placeholder="e.g. 3"
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Education-Specific Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <GraduationCap className="w-5 h-5 text-indigo-600" />
              Education Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Job Category</Label>
                <Select value={formData.job_category} onValueChange={(v) => {
                  handleChange('job_category', v);
                  handleChange('job_subcategory', '');
                }}>
                  <SelectTrigger className="mt-1" data-testid="job-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teaching">Teaching Positions</SelectItem>
                    <SelectItem value="non_teaching">Non-Teaching Positions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Position Type</Label>
                <Select value={formData.job_subcategory} onValueChange={(v) => handleChange('job_subcategory', v)}>
                  <SelectTrigger className="mt-1" data-testid="job-subcategory-select">
                    <SelectValue placeholder="Select position type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getSubcategories().map(sub => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subjects */}
            {formData.job_category === 'teaching' && (
              <div>
                <Label>Subjects to Teach</Label>
                <div className="flex flex-wrap gap-2 mt-2 p-3 border rounded-lg bg-slate-50 max-h-48 overflow-y-auto">
                  {constants?.subjects?.map(subject => (
                    <Badge
                      key={subject}
                      variant={formData.subjects.includes(subject) ? "default" : "outline"}
                      className="cursor-pointer transition-all"
                      onClick={() => handleMultiSelect('subjects', subject)}
                    >
                      {subject}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Grade Levels */}
            <div>
              <Label>Grade Levels</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {constants?.grade_levels?.map(level => (
                  <Badge
                    key={level}
                    variant={formData.grade_levels.includes(level) ? "default" : "outline"}
                    className="cursor-pointer transition-all"
                    onClick={() => handleMultiSelect('grade_levels', level)}
                  >
                    {level}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Board/Curriculum */}
            <div>
              <Label>Board / Curriculum</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {constants?.boards_curriculum?.map(board => (
                  <Badge
                    key={board}
                    variant={formData.board_curriculum.includes(board) ? "default" : "outline"}
                    className="cursor-pointer transition-all"
                    onClick={() => handleMultiSelect('board_curriculum', board)}
                  >
                    {board}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Required Certifications */}
            <div>
              <Label>Required Certifications</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {constants?.certifications?.map(cert => (
                  <Badge
                    key={cert}
                    variant={formData.required_certifications.includes(cert) ? "default" : "outline"}
                    className="cursor-pointer transition-all"
                    onClick={() => handleMultiSelect('required_certifications', cert)}
                  >
                    {cert}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap gap-6 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="demo_lesson"
                  checked={formData.demo_lesson_required}
                  onCheckedChange={(checked) => handleChange('demo_lesson_required', checked)}
                />
                <Label htmlFor="demo_lesson" className="cursor-pointer">Demo Lesson Required</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="accommodation"
                  checked={formData.accommodation_provided}
                  onCheckedChange={(checked) => handleChange('accommodation_provided', checked)}
                />
                <Label htmlFor="accommodation" className="cursor-pointer">Accommodation Provided</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="transport"
                  checked={formData.transport_provided}
                  onCheckedChange={(checked) => handleChange('transport_provided', checked)}
                />
                <Label htmlFor="transport" className="cursor-pointer">Transport Provided</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Skills */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Outfit' }}>Additional Skills</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                placeholder="Add a skill (e.g. Classroom Management)"
                data-testid="skill-input"
              />
              <Button type="button" variant="outline" onClick={addSkill}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.skills.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.skills.map((skill, idx) => (
                  <Badge key={idx} variant="secondary" className="pl-3 pr-1 py-1">
                    {skill}
                    <button onClick={() => removeSkill(skill)} className="ml-2 hover:bg-slate-300 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="secondary" onClick={() => handleSubmit(false)} disabled={loading} data-testid="save-draft-btn">
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button onClick={() => handleSubmit(true)} disabled={loading} data-testid="publish-btn">
            <Send className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Publish Job'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateJobPage;
